import React, { useEffect, useRef } from "react";
import { X, Wallet, Layers } from "lucide-react";
import { formatRule } from "../utils/BenefitUtils";

const BenefitFullfilment = ({ benefit, open, onClose }) => {
  const dialogRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  if (!open || !benefit) return null;

  const benefitName =
    typeof benefit.name === "string" && benefit.name.trim()
      ? benefit.name
      : "Benefit Details";

  const fulfillments = Array.isArray(benefit.fulfillments)
    ? benefit.fulfillments
    : [];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-2 sm:px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="benefit-fulfillment-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        className="relative w-full max-w-2xl max-h-[85vh] bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-modal-in"
      >
        {/* Sticky header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 px-5 py-4 bg-white border-b border-[#EFEDF4]">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-widest text-[#6D28D9] mb-0.5">
              Benefit Fulfillments
            </p>
            <h3
              id="benefit-fulfillment-title"
              className="text-lg sm:text-xl font-bold text-[#14131A] truncate"
            >
              {benefitName}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 h-9 w-9 rounded-full flex items-center justify-center text-[#8A8798] hover:text-[#6D28D9] hover:bg-[#F7F5FB] transition-colors"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {/* Scrollable body — holds 1..n fulfillment sections */}
        <div className="overflow-y-auto px-3 sm:px-5 py-3 sm:py-4 flex flex-col gap-3">
          {fulfillments.length === 0 && (
            <p className="text-sm text-[#8A8798] italic text-center py-8">
              No fulfillment details available yet.
            </p>
          )}

          {fulfillments.map((fulfillment, idx) => {
            const skus = Array.isArray(fulfillment?.skus) ? fulfillment.skus : [];
            const rules = Array.isArray(fulfillment?.rules) ? fulfillment.rules : [];

            return (
              <div
                key={fulfillment?.id ?? idx}
                className="rounded-2xl border border-[#EFEDF4] bg-[#FBFAFD] p-3 sm:p-4"
              >
                {/* Fulfillment header */}
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="shrink-0 h-7 w-7 rounded-lg bg-white ring-1 ring-[#6D28D9]/20 flex items-center justify-center">
                      <Layers size={13} className="text-[#6D28D9]" strokeWidth={2.25} />
                    </span>
                    <p className="text-sm sm:text-base font-bold text-[#14131A] truncate">
                      {fulfillment?.name || `Fulfillment ${idx + 1}`}
                    </p>
                  </div>
                  {fulfillments.length > 1 && (
                    <span className="shrink-0 text-[11px] font-semibold text-[#8A8798] bg-white rounded-full px-2 py-0.5 ring-1 ring-[#E8E5F0]">
                      {idx + 1} / {fulfillments.length}
                    </span>
                  )}
                </div>

                {/* Rule chips */}
                {rules.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {rules.flatMap((rule, rIdx) =>
                      formatRule(rule).map((chip, cIdx) => (
                        <span
                          key={`${rule?.uuid ?? rIdx}-${cIdx}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#6D28D9] bg-[#6D28D9]/8 ring-1 ring-[#6D28D9]/15 rounded-full px-2.5 py-1"
                        >
                          <Wallet size={11} strokeWidth={2.25} />
                          {chip.label}: {chip.value}
                        </span>
                      ))
                    )}
                  </div>
                )}

                {/* SKUs — the actual services covered */}
                {skus.length > 0 && (
                  <ul className="flex flex-col gap-1.5">
                    {skus.map((sku, sIdx) => (
                      <li
                        key={`${sku?.code ?? sIdx}`}
                        className="flex items-start justify-between gap-3 text-sm bg-white rounded-xl px-3 py-2 ring-1 ring-[#EFEDF4]"
                      >
                        <span className="text-[#14131A] break-words">{sku?.name}</span>
                        {sku?.serviceName && (
                          <span className="shrink-0 text-[11px] font-semibold text-[#8A8798]">
                            {sku.serviceName}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes modal-in {
          from { opacity: 0; transform: translateY(12px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-modal-in {
          animation: modal-in 220ms ease-out;
        }
        @media (max-width: 480px) {
          .animate-modal-in {
            max-height: 90vh;
          }
        }
      `}</style>
    </div>
  );
};

export default BenefitFullfilment;